import React, { useState, useEffect, useCallback } from 'react';

// --- Reusable UI Components ---

/**
 * A simple loading spinner component.
 */
const Spinner = () => (
  <div className="flex justify-center items-center p-8">
    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
  </div>
);

/**
 * A component to display API or network error messages.
 */
const ErrorMessage = ({ message }) => (
  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative my-4" role="alert">
    <strong className="font-bold">Error: </strong>
    <span className="block sm:inline">{message}</span>
  </div>
);

// --- Main Components ---

/**
 * Displays a table of mortgage rates for a specific scenario.
 * @param {{
 * title: string;
 * results: any[];
 * previousResults: any[];
 * }} props
 */
const RateTable = ({ title, results, previousResults }) => {
  // Checks if a rate has changed compared to the previous fetch.
  const hasRateChanged = (currentResult) => {
    if (!previousResults || previousResults.length === 0) return false;
    const prev = previousResults.find(p => p.lender_code === currentResult.lender_code);
    return prev && prev.initial_pay_rate !== currentResult.initial_pay_rate;
  };

  // Safely formats a value as a number with two decimal places.
  const formatNumber = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) ? num.toFixed(2) : 'N/A';
  };

  // Utility: truncate text to 10 chars + tooltip
  const truncateText = (text, maxLength = 10) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '..' : text;
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
      <div className="bg-gray-800 dark:bg-gray-900 text-white p-4">
        <h2 className="text-xl font-bold text-center">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        {results.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 p-6">No rates available for these criteria.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {['Lender', 'Rate (%)', 'Monthly (£)', 'True Cost (£)', 'Term (Months)', 'Fees (£)', 'Cashback (£)', 'Product', 'Lender Code'].map(header => (
                  <th key={header} scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {results.map((rate, index) => {
                const isHalifax = rate.lender_name?.toLowerCase().includes('halifax');
                const rateChanged = hasRateChanged(rate);
                
                const rowClass = rateChanged ? 'bg-yellow-100 dark:bg-yellow-900' : '';
                const cellClass = isHalifax ? 'bg-blue-100 dark:bg-blue-900 font-semibold' : '';

                return (
                  <tr key={rate.lender_code + index} className={rowClass}>
                    <td className={`px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 ${cellClass}`} title={rate.lender_name}>
                      {truncateText(rate.lender_name)}
                    </td>
                    <td className={`px-3 py-3 text-sm text-gray-800 dark:text-gray-200 font-bold ${cellClass}`}>
                      {formatNumber(rate.initial_pay_rate)}
                    </td>
                    <td className={`px-3 py-3 text-sm text-gray-800 dark:text-gray-200 font-bold ${cellClass}`}>
                      {formatNumber(rate.initial_monthly_payment)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(rate.true_cost)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {rate.initial_rate_period_months}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatNumber(rate.fees_total)}
                    </td>
                    <td className="px-3 py-3 text-sm text-green-600 font-semibold">
                      {rate.cashback > 0 ? `£${rate.cashback}` : '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400" title={rate.product_name}>
                      {truncateText(rate.product_name)}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {rate.lender_code}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * The main dashboard component for fetching and displaying mortgage rates.
 */
export default function RealTimeInterestRates() {
  const [propertyValue, setPropertyValue] = useState(565000);
  const [loanTerm, setLoanTerm] = useState(37);
  const [ltv, setLtv] = useState(90);

  const loanAmount = Math.round(propertyValue * (ltv / 100));
  const deposit = propertyValue - loanAmount;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rateData, setRateData] = useState({});
  const [prevRateData, setPrevRateData] = useState({});

  const handlePropertyValueChange = (e) => {
    setPropertyValue(parseInt(e.target.value, 10) || 0);
  };
  
  const handleLtvChange = (e) => {
    setLtv(parseInt(e.target.value, 10) || 0);
  };

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPrevRateData(rateData); 

    const scenarios = {
      '5% Deposit (2yr Fixed)': { ltv: 95, fixedPeriod: 'two_year', type: 'standard' },
      '10% Deposit (2yr Fixed)': { ltv: 90, fixedPeriod: 'two_year', type: 'standard' },
      '15% Deposit (2yr Fixed)': { ltv: 85, fixedPeriod: 'two_year', type: 'standard' },
      '25% Deposit (2yr Fixed)': { ltv: 75, fixedPeriod: 'two_year', type: 'standard' },
      '5% Deposit (5yr Fixed)': { ltv: 95, fixedPeriod: 'five_year', type: 'standard' },
      '10% Deposit (5yr Fixed)': { ltv: 90, fixedPeriod: 'five_year', type: 'standard' },
      '15% Deposit (5yr Fixed)': { ltv: 85, fixedPeriod: 'five_year', type: 'standard' },
      'Buy to Let (25% Deposit, 2yr Fixed)': { ltv: 75, fixedPeriod: 'two_year', type: 'buy_to_let', paymentMethod: 'interest_only' },
    };
    
    try {
      const promises = Object.values(scenarios).map(params => {
        const requestBody = {
          interest_rates: {
            purchase_price: propertyValue,
            mortgage_type: params.type,
            loan_amount: Math.round(propertyValue * (params.ltv / 100)),
            mortgage_purpose: "purchase",
            payment_method: params.paymentMethod || "repayment",
            limit: 10,
            priority: "lowest_monthly_payment",
            unique: true,
            mortgage_opts: {
              type: "fixed",
              initial_period: params.fixedPeriod,
              term: loanTerm,
            }
          }
        };

        return fetch("https://api.temboapi.com/api/v1/interest-rates", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        }).then(response => {
            if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
            return response.json();
        });
      });

      const responses = await Promise.all(promises);
      
      const newRateData = {};
      Object.keys(scenarios).forEach((title, index) => {
          const results = responses[index].results || [];
          results.sort((a, b) => a.initial_pay_rate - b.initial_pay_rate);
          newRateData[title] = results;
      });
      setRateData(newRateData);

    } catch (err) {
      console.error("Failed to fetch rates:", err);
      setError(`Could not fetch mortgage rates. This is likely a CORS security issue or a network problem. Please check the browser's developer console (F12) for more details.`);
    } finally {
      setLoading(false);
    }
  }, [propertyValue, loanTerm, rateData]);

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-gray-100 dark:bg-gray-900 font-sans min-h-screen">
      <header className="bg-gray-800 dark:bg-gray-900 text-white shadow-md">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h2 className="text-3xl font-bold">Live Mortgage Rate Dashboard</h2>
          <p className="text-gray-300 dark:text-gray-400">An interactive tool to compare UK mortgage products in real-time.</p>
        </div>
      </header>
      
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg mb-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Adjust Your Mortgage Scenario</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-end">
            <div>
              <label htmlFor="propertyValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Property Value (£)</label>
              <input
                type="number"
                id="propertyValue"
                value={propertyValue}
                onChange={handlePropertyValueChange}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor="loanTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Loan Term (Years)</label>
              <input
                type="number"
                id="loanTerm"
                value={loanTerm}
                onChange={(e) => setLoanTerm(parseInt(e.target.value, 10) || 0)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="lg:col-span-2">
              <label htmlFor="ltv" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Loan to Value (LTV): <strong className="text-indigo-600 dark:text-indigo-400">{ltv}%</strong></label>
              <input
                type="range"
                id="ltv"
                min="50" max="100" step="1"
                value={ltv}
                onChange={handleLtvChange}
                className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-center md:text-left text-gray-700 dark:text-gray-300">
              <p><strong>Deposit Required:</strong> £{deposit.toLocaleString()}</p>
              <p><strong>Total Loan Amount:</strong> £{loanAmount.toLocaleString()}</p>
          </div>
          <div className="mt-6 flex justify-center">
            <button
              onClick={fetchRates}
              disabled={loading}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-transform transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Fetching...' : 'Get Latest Rates'}
            </button>
          </div>
        </div>

        {loading && <Spinner />}
        {error && <ErrorMessage message={error} />}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.entries(rateData).map(([title, results]) => (
              <RateTable 
                key={title} 
                title={title} 
                results={results}
                previousResults={prevRateData[title] || []}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
